import { themeV2 } from '@features/lci-report/domain/theme-v2';
import { DEFAULT_FONT } from '@features/lci-report/utils/constants-v2';
import { contentSlideHeaderV2 } from '@features/lci-report/utils/slide-header-v2';
import type {
  Slide,
  ShapeProps,
  TextProps,
  ImageProps,
} from '@features/powerpoint';
import { MASTER_NAMES } from '@features/lci-report/utils/shared-presentation-config-v2';
import {
  howWeDoItStep01Content,
  howWeDoItStep02Content,
  howWeDoItStep03Content,
} from '../../images';

const BANNER_HEIGHT = 0.63;
const BANNER_Y_OFFSET = 0.32;
const CONTENT_Y_OFFSET = BANNER_Y_OFFSET + BANNER_HEIGHT;

const PANEL_GAP = 0.18;
const PANEL_MARGIN = 0.33;
const PANEL_W = (13.33 - 2 * PANEL_MARGIN - 2 * PANEL_GAP) / 3;
const PANEL_Y = 1.75;
const PANEL_H = 4.15;

const PANELS = [
  {
    step: 'Step 01',
    subtitle: 'Start with what we know.',
    contentImage: howWeDoItStep01Content,
    x: PANEL_MARGIN,
    y: PANEL_Y,
    w: PANEL_W,
    h: PANEL_H,
  },
  {
    step: 'Step 02',
    subtitle: 'Create a predictive model for exposed users',
    contentImage: howWeDoItStep02Content,
    x: PANEL_MARGIN + PANEL_W + PANEL_GAP,
    y: PANEL_Y,
    w: PANEL_W,
    h: PANEL_H,
  },
  {
    step: 'Step 03',
    subtitle: 'Measure & optimize results',
    contentImage: howWeDoItStep03Content,
    x: PANEL_MARGIN + 2 * (PANEL_W + PANEL_GAP),
    y: PANEL_Y,
    w: PANEL_W,
    h: PANEL_H,
  },
] as const;

const buildPanelShapes = (panel: (typeof PANELS)[number]): ShapeProps[] => [
  {
    shapeName: 'roundRect' as const,
    options: {
      x: panel.x,
      y: panel.y,
      w: panel.w,
      h: panel.h,
      fill: { color: themeV2.colors.white },
      line: { color: 'D0D0D0', width: 0.5 },
      rectRadius: 0.08,
    },
  },
  {
    shapeName: 'homePlate' as const,
    options: {
      x: panel.x + 0.02,
      y: panel.y + BANNER_Y_OFFSET,
      w: panel.w - 0.04,
      h: BANNER_HEIGHT,
      fill: { color: themeV2.colors.commandBlue },
    },
  },
];

const buildPanelTexts = (panel: (typeof PANELS)[number]): TextProps[] => [
  {
    text: [
      {
        text: panel.step,
        options: {
          fontSize: 18,
          fontFace: DEFAULT_FONT,
          bold: true,
          color: themeV2.colors.white,
          breakLine: true,
        },
      },
      {
        text: panel.subtitle,
        options: {
          fontSize: 9,
          fontFace: DEFAULT_FONT,
          color: themeV2.colors.white,
        },
      },
    ],
    options: {
      x: panel.x + 0.15,
      y: panel.y + BANNER_Y_OFFSET,
      w: panel.w - 0.6,
      h: BANNER_HEIGHT,
      valign: 'middle',
    },
  },
];

const buildPanelImage = (panel: (typeof PANELS)[number]): ImageProps => ({
  path: panel.contentImage,
  x: panel.x,
  y: panel.y + CONTENT_Y_OFFSET,
  w: panel.w,
  h: panel.h - CONTENT_Y_OFFSET,
});

export const howWeDoItSlideV2 = (): Slide => ({
  slideMaster: MASTER_NAMES.titleAndContent,
  shapes: PANELS.flatMap(panel => buildPanelShapes(panel)),
  images: PANELS.map(panel => buildPanelImage(panel)),
  texts: [
    contentSlideHeaderV2('How We Do It', {
      color: themeV2.colors.metricBlue,
    }),
    ...PANELS.flatMap(panel => buildPanelTexts(panel)),
  ],
});
